/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react';
import { useImmer } from 'use-immer';
import type { Validator } from './useInputValidation';

export interface Validation {
  isValid: boolean;
  message?: string;
  onFocus: () => void;
}

export interface Props {
  validation?: Validation;
}

export interface FormInput {
  name: string;
  validators: Validator<any>[];
  value: any;
  isValid: boolean;
  invalidMessge?: string;
  validationGroup?: string;
  onFocus: () => void;
}

interface State {
  inputs: FormInput[];
  validate: boolean;
}

interface Context {
  inputs: FormInput[];
  validate: boolean;
  addInput: (input: FormInput) => void;
  removeInput: (name: string) => void;
  updateInput: (input: FormInput) => void;
  isInputGroupValid: (group: string) => boolean;
  hasSubmitted: boolean;
}

export const FormContext = React.createContext<Context>({
  inputs: [],
  addInput: () => {},
  removeInput: () => {},
  updateInput: () => {},
  isInputGroupValid: () => true,
  validate: true,
  hasSubmitted: false,
});

export const Form: React.FC<React.HTMLProps<HTMLFormElement> & Props> = ({
  validation,
  children,
  ...rest
}) => {
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [{ inputs, validate }, setState] = useImmer<State>({
    inputs: [],
    validate: false,
  });

  const resetSubmit = React.useCallback(() => {
    setHasSubmitted(false);
  }, [setHasSubmitted]);

  /* Add input to watch for and run validators for */
  const addInput = (input: FormInput) => {
    setState((draft) => {
      draft.inputs.push(input);
    });
  };
  /* Remove input to watch for and run validators for */
  const removeInput = (name: string) => {
    setState((draft) => {
      draft.inputs = draft.inputs.filter((x) => x.name !== name);
    });
  };

  /* Runs validation on form submit */
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const errors: Validation[] = [];
    validateInputs(errors);
    setHasSubmitted(true);

    if (errors.length > 0) {
      setTimeout(() => errors[0].onFocus());
    } else if (rest.onSubmit) {
      rest.onSubmit(e);
    }
    return false;
  };

  /* Map through all registered inputs and run validators for them */
  const validateInputs = (errors: Validation[]) => {
    setState((draft) => {
      draft.validate = true;
      draft.inputs = draft.inputs.map((input) => {
        let errorMessage: string | undefined;
        input.isValid = input.validators.reduce(
          (current: boolean, validator) => {
            if (!validator.validation(input.value)) {
              errors.push({
                onFocus: input.onFocus,
                message: validator.message,
                isValid: false,
              });
              if (errorMessage === undefined) {
                errorMessage = validator.message;
              }

              return false;
            }

            return current;
          },
          true,
        );
        input.invalidMessge = errorMessage;
        return input;
      });
    });
  };

  /* update an already registered input */
  const updateInput = (input: FormInput) => {
    setState((draft) => {
      const index = draft.inputs.findIndex((x) => x.name === input.name);
      if (index > -1) {
        draft.inputs[index] = input;
      }
    });
  };

  const isInputGroupValid = (validationGroup: string) => {
    return inputs
      .filter((x) => x.validationGroup === validationGroup)
      .reduce((current, item) => current && item.isValid, true);
  };

  return (
    <FormContext.Provider
      value={{
        inputs,
        validate,
        removeInput,
        addInput,
        updateInput,
        isInputGroupValid,
        hasSubmitted,
      }}
    >
      <form {...rest} onSubmit={onSubmit} style={{ width: '100%' }}>
        {children}
      </form>
    </FormContext.Provider>
  );
};
